import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
} from "@angular/core";

import {
    Subject,
    map,
    pairwise,
    merge,
    startWith,
    combineLatest,
    scan,
    Observable,
} from "rxjs";

import { Asset, AssetSource } from "@hermes/api-model-asset";
import { Product } from "@hermes/api-model-product";

import { PRODUCT_PAGE_IMAGE_SIZE } from "@hermes/fragments/product-utils";
import { ProductImageSeoUrlPipe } from "@hermes/states/product-page";
import {
    AssetSourceValue,
    DOWN,
    LEFT,
    RIGHT,
    UP,
} from "@hermes/utils-generic/constants";

import {
    PanEvent,
    PinchEvent,
} from "@hermes/utils-generic/directives/gestures";

import { ProductImagePersoUrlPipe } from "../../pipes/product-image-perso-url.pipe";

import { ZoomService } from "../../services/zoom.service";

import {
    Coordinate,
    DeltaCoordinate,
    CursorDeltaCoordinate,
    Transform,
    TransformState,
} from "../../types/zoom.types";

// Step size for key press
const MOVE_STEP = 10;

/**
 * this component allows to zoom an image from a scale
 * it also manages the navigation event drag and drop (pan)
 */
@Component({
    selector: "h-product-image-zoom-image-deprecated",
    templateUrl: "./product-image-zoom-image-deprecated.component.html",
    styleUrls: ["./product-image-zoom-image-deprecated.component.scss"],
})
export class ProductImageZoomImageDeprecatedComponent
    implements AfterViewInit, OnChanges
{
    @Input()
    public product!: Product;
    /**
     * List of assets
     */
    @Input()
    public assets!: Asset[];

    /**
     * scale of zoom
     */
    @Input()
    public scale: number = 1;

    /**
     * current index of slelected image
     */
    @Input()
    public currentIndex: number = 0;

    /**
     * Content imgContainer
     */
    @ViewChild("imageZoomContainer")
    private contentElement!: ElementRef<HTMLElement>;

    @ViewChild("container")
    private containerRef!: ElementRef<HTMLDivElement>;

    /**
     * min scale of zoom
     */
    public minScale = 1;

    /**
     * max scale zoom
     */
    public maxScale = 2;

    /**
     * coordinate  after pan event
     */
    public lastCoordinate: Coordinate = { x: 0, y: 0 };

    /**
     * current coordinate of image
     */
    public currentCoordinate: Coordinate = { x: 0, y: 0 };
    /**
     * css transform (scale and translate)
     */
    public transforms: string = "";

    /**
     * image html element
     */
    public imgElement!: HTMLElement;

    /**
     * encoding quality (1…100 int).
     */
    public imgLowQualityLevel: number = 50;

    /**
     * encoding quality (1…100 int).
     */
    public imgHighQualityLevel: number = 99;

    /**
     * to ensure that during the pan, the image does not come out of the viewport entirely
     */
    public maxTranslateX!: number;
    public maxTranslateY!: number;

    /**
     * true when low quality image is loaded
     */
    public highQualityLoaded: boolean = false;
    /**
     * true when high quality image is loaded
     */
    public lowQualityLoaded: boolean = false;

    /**
     * just format the scale variable for the screen reader
     */
    public currentScale: number = 100;

    public size = PRODUCT_PAGE_IMAGE_SIZE;

    public transform$: Observable<Transform>;

    private initialCoordinate: Coordinate = { x: 0, y: 0 };

    private noDelta: DeltaCoordinate = { deltaX: 0, deltaY: 0 };
    private panFromkeyboard = new Subject<string>();
    private panFromCursor = new Subject<CursorDeltaCoordinate>();

    constructor(
        private productImageSeoUrlPipe: ProductImageSeoUrlPipe,
        private productImagePersoUrlPipe: ProductImagePersoUrlPipe,
        private readonly zoomService: ZoomService,
    ) {
        const scale$ = this.zoomService.scale$;
        const keyboardDelta$ = this.panFromkeyboard.pipe(
            map((key) => this.getAxisDeltaByKey(key)),
        );

        const cursorDelta$ = this.panFromCursor.pipe(
            pairwise(),
            map(([n, n1]) => this.getAxisDeltaByPanDelta(n, n1)),
        );

        const delta$ = merge(cursorDelta$, keyboardDelta$).pipe(
            startWith(this.noDelta),
        );

        this.transform$ = combineLatest([delta$, scale$]).pipe(
            scan(({ coordinate }, [axisDelta, scale]) => {
                if (this.zoomService.isMinScale(scale)) {
                    return this.setTransforms();
                }
                const position = this.panImage(coordinate, axisDelta);

                return this.setTransforms(position, scale);
            }, this.setTransforms()),
            map((state) => state.transform),
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.assets &&
            changes.assets.currentValue !== changes.assets.previousValue
        ) {
            this.scale = this.minScale;
            this.formatCurrentScale();

            this.transforms = "";
            this.highQualityLoaded = false;
            this.lowQualityLoaded = false;
        }

        if (
            changes.scale &&
            changes.scale.currentValue !== changes.scale.previousValue
        ) {
            this.zoom(this.scale);
        }
    }

    public ngAfterViewInit(): void {
        this.imgElement = this.contentElement.nativeElement;
        // at least 30% of the image must remain in the viewport
        const parentElement = this.imgElement.parentElement;
        if (parentElement) {
            this.maxTranslateX = parentElement.offsetWidth * 0.7;
            this.maxTranslateY = parentElement.offsetHeight * 0.7;
        }
    }

    public onPinchMove(event: PinchEvent) {
        this.zoomService.pinch(event);
    }
    public onPanMove(event: PanEvent) {
        this.panFromCursor.next(event);
    }

    /**
     * zoom image
     */
    public zoom(scale: number): void {
        this.scale = scale;
        if (scale <= this.minScale) {
            this.scale = this.minScale;
        }

        if (scale >= this.maxScale) {
            this.scale = this.maxScale;
        }
        this.zoomService.updateScale(this.scale);
    }

    public onPanEnd(): void {
        this.lastCoordinate = this.currentCoordinate;
    }

    public handleHighQualityLoaded(): void {
        this.highQualityLoaded = true;
    }

    public handleLowQualityLoaded(): void {
        this.lowQualityLoaded = true;
    }

    /**
     * Transform url based on product templateType
     */
    public getImageSrc(
        imageUrl: string,
        assetSource?: AssetSource,
        imageSize?: number,
        quality?: number,
    ): string | undefined {
        if (assetSource === AssetSourceValue.Platforme) {
            return this.productImagePersoUrlPipe.transform(imageUrl, 1200);
        }
        return this.productImageSeoUrlPipe.transform(imageUrl, {
            slug: this.product.slug,
            imageSize,
            quality,
            templateType: this.product.templateType,
        });
    }

    /**
     * Handle zoom with KeyboardEvent event
     */
    public onKeyDown(event: KeyboardEvent): void {
        const coordinate = this.updateCoordinate(event.key);

        if (
            this.currentCoordinate.x !== coordinate.x ||
            this.currentCoordinate.y !== coordinate.y
        ) {
            this.currentCoordinate = coordinate;
            this.transforms = `translate(${coordinate.x}px, ${coordinate.y}px) scale(${this.scale})`;
            this.onPanEnd();
        }
    }

    public setTransforms(
        coordinate?: Coordinate,
        scale?: number,
    ): TransformState {
        const { x, y } = coordinate || this.initialCoordinate;
        scale = scale || ZoomService.minScale;

        return {
            coordinate: { x, y },
            transform: `translate(${x}px, ${y}px) scale(${scale})`,
        };
    }

    private updateCoordinate(key: string): Coordinate {
        let x = this.lastCoordinate.x;
        let y = this.lastCoordinate.y;

        switch (key) {
            case UP: {
                y = Math.max(
                    this.lastCoordinate.y - MOVE_STEP,
                    -Math.abs(this.maxTranslateY),
                );
                break;
            }
            case DOWN: {
                y = Math.min(
                    this.lastCoordinate.y + MOVE_STEP,
                    this.maxTranslateY,
                );
                break;
            }
            case LEFT: {
                x = Math.max(
                    this.lastCoordinate.x - MOVE_STEP,
                    -Math.abs(this.maxTranslateX),
                );
                break;
            }
            case RIGHT: {
                x = Math.min(
                    this.lastCoordinate.x + MOVE_STEP,
                    this.maxTranslateX,
                );
                break;
            }
        }

        return { x, y };
    }

    private formatCurrentScale(): void {
        this.currentScale = this.scale * 100;
    }

    private getAxisDeltaByKey(key: string): DeltaCoordinate {
        switch (key) {
            case LEFT: {
                return this.deltaXY(-MOVE_STEP)(0);
            }
            case RIGHT: {
                return this.deltaXY(MOVE_STEP)(0);
            }
            case UP: {
                return this.deltaXY(0)(-MOVE_STEP);
            }
            case DOWN: {
                return this.deltaXY(0)(MOVE_STEP);
            }
            default: {
                return this.noDelta;
            }
        }
    }

    private deltaXY =
        (x: number = 0) =>
        (y: number = 0) => ({
            deltaX: x,
            deltaY: y,
        });

    private getAxisDeltaByPanDelta(
        previous: CursorDeltaCoordinate,
        current: CursorDeltaCoordinate,
    ) {
        if (previous.isFinal) {
            return this.noDelta;
        }
        const x = current.deltaX - previous.deltaX;
        const y = current.deltaY - previous.deltaY;

        return this.deltaXY(x)(y);
    }

    /**
     * Pan image and keep it visible at 30%
     */
    private panImage(position: Coordinate, pan: DeltaCoordinate): Coordinate {
        position.x += pan.deltaX;
        position.y += pan.deltaY;
        const boundary = this.containerRef.nativeElement as HTMLElement;

        return this.zoomService.restrictPosition(position, boundary);
    }
}
